import { useCallback, useEffect, useMemo, useState } from 'react'

import { api } from '../api'
import { ensureGender, mixedFree } from '../gender'
import { t } from '../i18n'
import { haptic, notify, openExternal } from '../telegram'
import DealsStrip from '../components/DealsStrip'
import Coaches, { CoachTile } from './Coaches'
import { isTournamentKind } from '../types'
import type { BookingClub, Club, CoachListItem, ExternalGame, Game, MyInvite, PlayerMe, Slot } from '../types'
import { euro as bookEuro } from './Booking'

interface Props {
  me: PlayerMe
  onCreate: () => void
  onOpen: (gameId: number) => void
  /** Заявка на корт в клубе с бронированием через Por4 (v0.35) */
  onBook: (clubId: number) => void
}

type Mode = 'level' | 'all' | 'mine'

// Выбор города и режима списка — удобство конкретного человека, живёт в его браузере
const CITY_KEY = 'por4.city'
const MODE_KEY = 'por4.feedMode'

function remember(key: string, value: string | null): void {
  try {
    if (value) localStorage.setItem(key, value)
    else localStorage.removeItem(key)
  } catch {
    /* хранилище недоступно — выбор проживёт до закрытия */
  }
}

function recall(key: string): string | null {
  try {
    return localStorage.getItem(key)
  } catch {
    return null
  }
}

function time(iso: string): string {
  return new Date(iso).toLocaleTimeString(t().locale, { hour: '2-digit', minute: '2-digit' })
}

/** Ключ дня в локальном времени: 2026-10-04 */
function isoDay(d: Date): string {
  const m = String(d.getMonth() + 1).padStart(2, '0')
  const day = String(d.getDate()).padStart(2, '0')
  return `${d.getFullYear()}-${m}-${day}`
}

/** «Сегодня», «Завтра», «пт 9» */
function whenLabel(iso: string): string {
  const d = new Date(iso)
  const today = new Date()
  const tomorrow = new Date(today)
  tomorrow.setDate(today.getDate() + 1)
  if (isoDay(d) === isoDay(today)) return t().today
  if (isoDay(d) === isoDay(tomorrow)) return t().tomorrow
  return `${t().weekdays[d.getDay()]} ${d.getDate()}`
}

function initials(name: string): string {
  return name.trim().slice(0, 1).toUpperCase() || '?'
}

function euro(value: number): string {
  return `${String(value).replace('.', ',')} €`
}

function Face({ slot, small = false }: { slot: Slot; small?: boolean }) {
  const p = slot.player
  return p.photo_url ? (
    <img className={`face${small ? ' small' : ''}`} src={p.photo_url} alt="" />
  ) : (
    <span className={`face${small ? ' small' : ''}`}>{initials(p.first_name)}</span>
  )
}

interface Item {
  at: string
  game?: Game
  ext?: ExternalGame
}

/** Открытый матч клуба из Playtomic. Записаться можно только там — у нас
 *  карточка-указатель: где, когда, сколько мест и какого уровня игроки. */
function ExternalCard({ game }: { game: ExternalGame }) {
  const url = game.url
  return (
    <div className="card external">
      <div className="row between">
        <div>
          <div className="badge source" style={{ marginBottom: 4 }}>
            Playtomic
          </div>
          <div className="g-when">{whenLabel(game.starts_at)}</div>
          <div className="g-time">{time(game.starts_at)}</div>
          <div className="muted">
            {game.club.name} · {game.club.city}
          </div>
          <div className="muted small">
            {game.level_min != null && game.level_max != null
              ? t().externalLevel(game.level_min, game.level_max)
              : t().externalNoLevel}
          </div>
        </div>
        <span className="badge">{t().freeSlots(game.free)}</span>
      </div>
      <div className="row between" style={{ marginTop: 12 }}>
        <div className="muted small">{t().externalHint}</div>
        {url && (
          <button className="ghost" style={{ flex: '0 0 auto' }} onClick={() => openExternal(url)}>
            {t().openInPlaytomic}
          </button>
        )}
      </div>
    </div>
  )
}

/** Корт: состав по двое с каждой стороны сетки. Пары до игры никто не
 *  назначал — это просто четыре места, поэтому подпись «Состав», без «VS». */
function Court({ game, me }: { game: Game; me: PlayerMe }) {
  const joined = game.slots.filter((s) => s.status === 'joined')
  const free = Math.max(game.max_players - joined.length, 0)
  const invite = !game.my_status && game.fits_my_level && free > 0
  let cells: (Slot | 'you' | 'free')[] = [
    ...joined,
    ...Array.from({ length: free }, (_, i) => (i === 0 && invite ? 'you' : 'free') as 'you' | 'free'),
  ]
  if (game.max_players === 4 && joined.some((s) => s.court_spot)) {
    // Игроки уже выбрали места: пара 1 слева от сетки, пара 2 справа
    const placed: (Slot | 'you' | 'free' | null)[] = [null, null, null, null]
    for (const s of joined) if (s.court_spot) placed[s.court_spot - 1] = s
    const rest = cells.filter((c) => c === 'you' || c === 'free' || !c.court_spot)
    cells = placed.map((c) => c ?? rest.shift() ?? 'free')
  }
  const cell = (c: Slot | 'you' | 'free', i: number) =>
    c === 'you' || c === 'free' ? (
      <div key={`e${i}`} className="spot free">
        <span className="face empty">+</span>
        <span className="spot-name">{c === 'you' ? t().homeYou : t().homeFree}</span>
        <span className="spot-level">{c === 'you' ? me.level?.toFixed(2) : ' '}</span>
      </div>
    ) : (
      <div key={c.id} className="spot">
        <Face slot={c} />
        <span className="spot-name">{c.player.first_name}</span>
        <span className="spot-level">{c.player.level?.toFixed(2) ?? '—'}</span>
      </div>
    )
  if (game.max_players !== 4) {
    return <div className="court many">{cells.slice(0, 8).map(cell)}</div>
  }
  return (
    <div className="court" aria-label={t().homeRoster}>
      <div className="side">{cells.slice(0, 2).map(cell)}</div>
      <div className="net" aria-hidden />
      <div className="side">{cells.slice(2, 4).map((c, i) => cell(c, i + 2))}</div>
    </div>
  )
}

function GameCard({
  game,
  me,
  busy,
  onOpen,
  onJoin,
}: {
  game: Game
  me: PlayerMe
  busy: boolean
  onOpen: () => void
  onJoin: () => void
}) {
  const free = Math.max(game.max_players - game.joined_count, 0)
  return (
    <div className="card gcard">
      <button className="gcard-open" onClick={onOpen} aria-label={`${time(game.starts_at)} ${game.club.name}`}>
        <div className="row between" style={{ alignItems: 'flex-start' }}>
          <div style={{ minWidth: 0 }}>
            <div className="g-when">{whenLabel(game.starts_at)}</div>
            <div className="g-time">{time(game.starts_at)}</div>
            <div className="muted g-club">
              {game.club.name} · {game.club.city}
            </div>
            <div className="muted small">
              {t().level} {game.level_min.toFixed(2)}–{game.level_max.toFixed(2)}
            </div>
          </div>
          {game.price_per_player != null && (
            <div className="g-price">
              <b>{euro(game.price_per_player)}</b>
              <span className="muted small">{t().perPlayer}</span>
            </div>
          )}
        </div>
        <Court game={game} me={me} />
      </button>
      <div className="row between" style={{ gap: 10 }}>
        <div className="g-tags">
          {game.mixed ? (
            <span className="badge">{t().mixedFree(mixedFree(game).m, mixedFree(game).f)}</span>
          ) : free > 0 ? (
            <span className="badge">{t().freeSlots(free)}</span>
          ) : (
            <span className="badge neutral">{t().full}</span>
          )}
          {!game.rated && <span className="badge neutral">{t().friendlyBadge}</span>}
          {game.waitlist_count > 0 && <span className="badge neutral">{t().inWaitlist(game.waitlist_count)}</span>}
          {game.chat_unread > 0 && <span className="badge unread">💬 {game.chat_unread}</span>}
        </div>
        {game.my_status ? (
          <button className="ghost g-btn" onClick={onOpen}>
            {game.my_status === 'joined' ? t().youAreIn : t().youAreWaiting}
          </button>
        ) : (
          <button className={`g-btn${free > 0 ? '' : ' ghost'}`} disabled={busy} onClick={onJoin}>
            {free > 0 ? t().join : t().waitlist}
          </button>
        )}
      </div>
      {!game.fits_my_level && !game.my_status && <div className="muted small" style={{ marginTop: 8 }}>{t().levelWarning}</div>}
    </div>
  )
}

/** Моя ближайшая игра — крупно, зелёной карточкой. */
function NextGame({ game, onOpen }: { game: Game; onOpen: () => void }) {
  const joined = game.slots.filter((s) => s.status === 'joined')
  const free = Math.max(game.max_players - joined.length, 0)
  const weekday = new Date(game.starts_at).toLocaleDateString(t().locale, { weekday: 'long' })
  return (
    <button className="next-game" onClick={onOpen}>
      <span className="ng-when">
        {whenLabel(game.starts_at)} · {weekday}
      </span>
      <span className="ng-time">{time(game.starts_at)}</span>
      <span className="ng-club">
        {game.club.name} · {game.club.city}
        {game.court_booked && ` · ${t().courtBookedShort}`}
      </span>
      <span className="ng-row">
        <span className="ng-faces">
          {joined.slice(0, 6).map((s) => (
            <Face key={s.id} slot={s} small />
          ))}
          {Array.from({ length: Math.min(free, 4) }).map((_, i) => (
            <span key={i} className="face small empty">
              +
            </span>
          ))}
        </span>
        <span className="ng-pills">
          {game.kind === 'training' ? <span className="ng-pill">🎓</span> : game.kind !== 'game' && <span className="ng-pill">🏆</span>}
          {game.my_status === 'waitlist' && <span className="ng-pill">{t().youAreWaiting}</span>}
          <span className="ng-pill">
            {joined.length}/{game.max_players}
          </span>
          {game.chat_unread > 0 && <span className="ng-chat">💬 {game.chat_unread}</span>}
        </span>
      </span>
    </button>
  )
}

/** Групповая тренировка: тренер, время, места. В ленте на главной и в «Моих». */
function TrainingCard({ game, onOpen, wide = false }: { game: Game; onOpen: () => void; wide?: boolean }) {
  const share = Math.min(game.joined_count / Math.max(game.max_players, 1), 1)
  return (
    <button className={`tour training${wide ? ' wide' : ''}`} onClick={onOpen}>
      <span className="row between" style={{ gap: 8 }}>
        <span className="badge gold">{t().trainingBadge}</span>
        {game.my_status && <span className="badge">{game.my_status === 'joined' ? t().youAreIn : t().youAreWaiting}</span>}
      </span>
      <span className="tour-time">
        {whenLabel(game.starts_at)} · {time(game.starts_at)}
      </span>
      <span className="small">
        {t().coachLabel}: <b>{game.captain.first_name}</b>
      </span>
      <span className="muted small">
        {game.club.name} · {t().level} {game.level_min.toFixed(1)}–{game.level_max.toFixed(1)}
      </span>
      <span className="row between" style={{ marginTop: 10, alignItems: 'flex-end' }}>
        <span>
          <span className="muted small">{t().homeSeats(game.joined_count, game.max_players)}</span>
          <span className="bar">
            <span style={{ width: `${share * 100}%` }} />
          </span>
        </span>
        {game.price_per_player != null && <b>{euro(game.price_per_player)}</b>}
      </span>
    </button>
  )
}

function TournamentCard({ game, onOpen }: { game: Game; onOpen: () => void }) {
  const share = Math.min(game.joined_count / Math.max(game.max_players, 1), 1)
  return (
    <button className="tour" onClick={onOpen}>
      <span className="badge gold">{game.mixed ? t().mixedBadge : t().tournamentBadge(game.kind)}</span>
      <span className="tour-time">
        {whenLabel(game.starts_at)} · {time(game.starts_at)}
      </span>
      <span className="muted small">
        {game.club.name} · {t().level} {game.level_min.toFixed(1)}–{game.level_max.toFixed(1)}
      </span>
      <span className="row between" style={{ marginTop: 10, alignItems: 'flex-end' }}>
        <span>
          <span className="muted small">
            {game.status === 'running' ? t().statusRunning : t().homeSeats(game.joined_count, game.max_players)}
          </span>
          <span className="bar">
            <span style={{ width: `${share * 100}%` }} />
          </span>
        </span>
        {game.price_per_player != null && <b>{euro(game.price_per_player)}</b>}
      </span>
    </button>
  )
}

export default function Feed({ me, onCreate, onOpen, onBook }: Props) {
  const [games, setGames] = useState<Game[] | null>(null)
  // Открытые матчи клубов из Playtomic: витрина, запись — там
  const [external, setExternal] = useState<ExternalGame[]>([])
  const [invites, setInvites] = useState<MyInvite[]>([])
  const [clubs, setClubs] = useState<Club[]>([])
  const [mode, setModeState] = useState<Mode>(() => (recall(MODE_KEY) as Mode) || 'level')
  const [city, setCityState] = useState<string | null>(() => recall(CITY_KEY))
  const [day, setDay] = useState<string | null>(null)
  const [busy, setBusy] = useState<number | null>(null)
  const [coaches, setCoaches] = useState<CoachListItem[]>([])
  const [bookClubs, setBookClubs] = useState<BookingClub[]>([])
  // Раздел «Тренеры» поверх ленты: undefined — закрыт, null — список, число — страница тренера
  const [coachView, setCoachView] = useState<number | null | undefined>(undefined)

  const setMode = (m: Mode) => {
    setModeState(m)
    remember(MODE_KEY, m)
  }
  const setCity = (c: string | null) => {
    setCityState(c)
    remember(CITY_KEY, c)
  }

  const load = useCallback(async () => {
    // Всё, кроме своих игр, — дополнение: сбой не должен ломать ленту
    const [own, ext, inv, coachList] = await Promise.all([
      api.games({ days: 14 }),
      api.externalGames().catch(() => [] as ExternalGame[]),
      api.myInvites().catch(() => [] as MyInvite[]),
      api.coaches().catch(() => [] as CoachListItem[]),
    ])
    setExternal(ext)
    setInvites(inv)
    setCoaches(coachList)
    setGames(own)
  }, [])

  useEffect(() => {
    void load()
    api.clubs().then(setClubs, () => undefined)
    api.bookingClubs().then(setBookClubs, () => undefined)
  }, [load])

  const cities = useMemo(() => Array.from(new Set(clubs.map((c) => c.city))).sort(), [clubs])
  const inCity = useCallback((c: { city: string }) => !city || c.city === city, [city])

  const all = useMemo(() => (games ?? []).filter((g) => inCity(g.club)), [games, inCity])
  // Мои — где я в составе или резерве, а у тренера ещё и тренировки, которые он ведёт
  const mine = useMemo(() => all.filter((g) => g.my_status || (g.kind === 'training' && g.is_captain)), [all])
  const next = mine[0] ?? null

  const week = useMemo(() => {
    const today = new Date()
    return Array.from({ length: 7 }, (_, i) => {
      const d = new Date(today)
      d.setDate(today.getDate() + i)
      return d
    })
  }, [])
  const myDays = useMemo(() => new Set(mine.map((g) => isoDay(new Date(g.starts_at)))), [mine])
  const onDay = useCallback((iso: string) => !day || isoDay(new Date(iso)) === day, [day])

  const tournaments = useMemo(
    () => all.filter((g) => isTournamentKind(g.kind) && !g.my_status && onDay(g.starts_at)),
    [all, onDay],
  )
  const trainings = useMemo(
    () => all.filter((g) => g.kind === 'training' && !g.my_status && !g.is_captain && onDay(g.starts_at)),
    [all, onDay],
  )
  const localCoaches = useMemo(
    () => coaches.filter((c) => !city || c.clubs.some((club) => club.city === city)),
    [coaches, city],
  )

  // «Только мой уровень» для матчей Playtomic: уровень их игроков известен и я в него попадаю (±0.5)
  const shownExternal = useMemo(
    () =>
      mode === 'mine'
        ? []
        : external.filter((g) => {
            if (!inCity(g.club) || !onDay(g.starts_at)) return false
            if (mode === 'all') return true
            if (g.level_min == null || g.level_max == null || me.level == null) return false
            return me.level >= g.level_min - 0.5 && me.level <= g.level_max + 0.5
          }),
    [external, mode, inCity, onDay, me.level],
  )

  const items = useMemo<Item[]>(() => {
    const own = all.filter((g) => {
      if (!onDay(g.starts_at)) return false
      if (mode === 'mine') return Boolean(g.my_status) || (g.kind === 'training' && g.is_captain)
      if (g.kind !== 'game' || g.my_status) return false // турниры — в своей ленте, мои — сверху
      return mode === 'all' || g.fits_my_level
    })
    return [
      ...own.map((game): Item => ({ at: game.starts_at, game })),
      ...shownExternal.map((ext): Item => ({ at: ext.starts_at, ext })),
    ].sort((a, b) => new Date(a.at).getTime() - new Date(b.at).getTime())
  }, [all, mode, onDay, shownExternal])

  // Быстрая запись прямо из ленты. Отмена и всё остальное — на экране игры,
  // где видно, кого вы подводите.
  async function quickJoin(game: Game) {
    setBusy(game.id)
    haptic()
    try {
      // В микст нужно знать, мужское место занимаем или женское
      if (game.mixed && !(await ensureGender(me))) return
      await api.join(game.id)
      await load()
    } catch (e) {
      notify(e instanceof Error ? e.message : 'Ошибка')
    } finally {
      setBusy(null)
    }
  }

  if (!games) return <p className="muted">{t().loading}</p>

  const invite = invites[0]
  const today = isoDay(new Date())

  return (
    <div className="home">
      <div className="home-top">
        <span className="home-brand">
          Por<b>4</b>
        </span>
        <label className="city-pill">
          <span aria-hidden>📍</span>
          <select
            value={city ?? ''}
            aria-label={t().homeCity}
            onChange={(e) => {
              haptic()
              setCity(e.target.value || null)
            }}
          >
            <option value="">{t().homeAllTowns}</option>
            {cities.map((c) => (
              <option key={c} value={c}>
                {c}
              </option>
            ))}
          </select>
          <span className="chev" aria-hidden>
            ▾
          </span>
        </label>
        <span className="home-level">
          <small>{t().homeYourLevel}</small>
          <b>{me.level?.toFixed(2) ?? '—'}</b>
        </span>
      </div>

      <div className="week" role="group" aria-label={t().homeWeek}>
        {week.map((d) => {
          const key = isoDay(d)
          return (
            <button
              key={key}
              className={`wday${key === today ? ' today' : ''}`}
              aria-pressed={day === key}
              onClick={() => {
                haptic()
                setDay((cur) => (cur === key ? null : key))
              }}
            >
              <small>{t().weekdays[d.getDay()]}</small>
              <span>{d.getDate()}</span>
              {myDays.has(key) && <i aria-label={t().homeHaveGame} />}
            </button>
          )
        })}
      </div>

      <div className="home-h">
        <span>{t().homeNext}</span>
        {mine.length > 1 && (
          <button className="link" onClick={() => setMode('mine')}>
            {t().homeAllMine(mine.length)}
          </button>
        )}
      </div>
      {next ? (
        <NextGame game={next} onOpen={() => onOpen(next.id)} />
      ) : (
        <div className="next-empty">
          <span className="muted">{t().homeNoNext}</span>
          <button className="ghost" onClick={onCreate}>
            {t().createGame}
          </button>
        </div>
      )}

      {invite && (
        <button className="invite-pill" onClick={() => onOpen(invite.game.id)}>
          <span className="ip-ic" aria-hidden>
            ✉️
          </span>
          <span style={{ minWidth: 0, flex: 1 }}>
            <b>{t().homeInvites(invite.inviter_name)}</b>
            <span className="muted small">
              {whenLabel(invite.game.starts_at)} · {time(invite.game.starts_at)} · {invite.game.club.name}
            </span>
          </span>
          <span className="count">{invites.length}</span>
        </button>
      )}

      {bookClubs.filter(inCity).map((c) => {
        const p90 = c.prices.find((p) => p.minutes === 90 && p.amount != null) ?? c.prices.find((p) => p.amount != null)
        return (
          <button key={c.id} className="invite-pill book-pill" onClick={() => onBook(c.id)}>
            <span className="ip-ic" aria-hidden>
              🎾
            </span>
            <span style={{ minWidth: 0, flex: 1 }}>
              <b>
                {t().bookHome} · {c.name}
                {!c.public && <span className="badge neutral" style={{ marginLeft: 6 }}>{t().bookDemo}</span>}
              </b>
              <span className="muted small">
                {c.city} · {t().bookHomeHint(c.courts.length, p90 ? `${bookEuro(p90.amount)} / ${t().bookHours(p90.minutes)}` : '')}
              </span>
            </span>
            <span aria-hidden className="muted">›</span>
          </button>
        )
      })}

      {(trainings.length > 0 || localCoaches.length > 0) && (
        <>
          <div className="home-h">
            <span>{t().coachesHome}</span>
            {coaches.length > 0 && (
              <button className="link" onClick={() => setCoachView(null)}>
                {t().coachesAll} · {coaches.length}
              </button>
            )}
          </div>
          <div className="rail">
            {trainings.map((g) => (
              <TrainingCard key={g.id} game={g} onOpen={() => onOpen(g.id)} />
            ))}
            {localCoaches.map((c) => (
              <CoachTile key={c.id} coach={c} onOpen={() => setCoachView(c.id)} />
            ))}
          </div>
        </>
      )}

      {tournaments.length > 0 && (
        <>
          <div className="home-h">
            <span>{t().homeTournaments}</span>
          </div>
          <div className="rail">
            {tournaments.map((g) => (
              <TournamentCard key={g.id} game={g} onOpen={() => onOpen(g.id)} />
            ))}
          </div>
        </>
      )}

      <div className="home-h">
        <span>{mode === 'mine' ? t().homeMine : t().homeOpen}</span>
      </div>
      <div className="seg wide" role="group">
        {(['level', 'all', 'mine'] as Mode[]).map((m) => (
          <button key={m} aria-pressed={mode === m} onClick={() => setMode(m)}>
            {m === 'level' ? t().homeSegLevel : m === 'all' ? t().homeSegAll : t().homeSegMine}
          </button>
        ))}
      </div>

      {items.length === 0 && (
        <div className="card" style={{ textAlign: 'center', padding: '24px 18px' }}>
          <div className="title">{day ? t().noGamesOnDay : t().homeNothing}</div>
          <button style={{ marginTop: 14 }} onClick={onCreate}>
            {t().createGame}
          </button>
        </div>
      )}

      {items.map((item, index) => {
        // Полоса скидок — после третьей карточки, чтобы не заслонять игры
        const strip = index === Math.min(2, items.length - 1) ? <DealsStrip key="deals" /> : null
        return (
          <div key={item.game ? item.game.id : `ext-${item.ext!.id}`}>
            {item.ext ? (
              <ExternalCard game={item.ext} />
            ) : item.game!.kind === 'training' ? (
              <TrainingCard game={item.game!} wide onOpen={() => onOpen(item.game!.id)} />
            ) : (
              <GameCard
                game={item.game!}
                me={me}
                busy={busy === item.game!.id}
                onOpen={() => onOpen(item.game!.id)}
                onJoin={() => quickJoin(item.game!)}
              />
            )}
            {strip}
          </div>
        )
      })}

      {items.length === 0 && <DealsStrip />}

      {coachView !== undefined && (
        <Coaches
          me={me}
          trainings={games ?? []}
          onClose={() => {
            setCoachView(undefined)
            void load()
          }}
          onOpenGame={(id) => {
            setCoachView(undefined)
            onOpen(id)
          }}
          key={coachView ?? 'list'}
          openFirst={coachView}
        />
      )}
    </div>
  )
}
